import { useEffect, useState, useCallback } from "react";
import { Plus, Edit2, Trash2, Calendar, MapPin, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { formatShortDate } from "../../lib/dateUtils";

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    date: "",
    location: "",
    external_url: "",
    description: "",
  });
  const [saving, setSaving] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const data = await api.getEvents();
      setEvents(data);
    } catch {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const openCreateDialog = () => {
    setEditingEvent(null);
    setFormData({
      title: "",
      date: new Date().toISOString().split('T')[0],
      location: "",
      external_url: "",
      description: "",
    });
    setDialogOpen(true);
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      date: event.date,
      location: event.location,
      external_url: event.external_url || "",
      description: event.description || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingEvent) {
        await api.updateEvent(editingEvent.id, formData);
        toast.success("Event updated successfully");
      } else {
        await api.createEvent(formData);
        toast.success("Event created successfully");
      }
      setDialogOpen(false);
      fetchEvents();
    } catch {
      toast.error("Failed to save event");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (event) => {
    if (!window.confirm(`Are you sure you want to delete "${event.title}"?`)) return;

    try {
      await api.deleteEvent(event.id);
      toast.success("Event deleted successfully");
      fetchEvents();
    } catch {
      toast.error("Failed to delete event");
    }
  };

  const today = new Date().toISOString().split('T')[0];

  let saveButtonLabel;
  if (saving) {
    saveButtonLabel = "Saving...";
  } else if (editingEvent) {
    saveButtonLabel = "Update";
  } else {
    saveButtonLabel = "Create";
  }

  return (
    <div data-testid="admin-events-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-russo text-3xl text-white uppercase tracking-tighter">
            EVENTS
          </h1>
          <p className="font-mono text-white/50 mt-2">
            Manage upcoming and past events
          </p>
        </div>
        <button
          onClick={openCreateDialog}
          className="flex items-center gap-2 px-6 py-3 bg-demolition-red text-white font-teko text-lg uppercase tracking-wider hover:bg-demolition-red-hover transition-colors"
          data-testid="create-event-btn"
        >
          <Plus className="w-5 h-5" />
          Add Event
        </button>
      </div>

      {/* Events List */}
      {loading && (
        <div className="text-center py-12">
          <p className="font-mono text-white/50 animate-pulse">Loading events...</p>
        </div>
      )}
      {!loading && events.length > 0 && (
        <div className="space-y-4">
          {events.map((event) => (
            <div
              key={event.id}
              className={`bg-demolition-paper border border-white/10 p-6 ${
                event.date < today ? 'opacity-60' : ''
              }`}
              data-testid={`event-row-${event.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {event.date >= today ? (
                      <span className="px-2 py-1 bg-demolition-red text-white font-teko text-xs uppercase">
                        Upcoming
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-white/10 text-white/50 font-teko text-xs uppercase">
                        Past
                      </span>
                    )}
                  </div>
                  <h3 className="font-teko text-xl text-white uppercase tracking-wide">
                    {event.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-4 mt-2 text-white/50">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span className="font-mono text-sm">{formatShortDate(event.date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span className="font-mono text-sm">{event.location}</span>
                    </div>
                    {event.external_url && (
                      <a
                        href={event.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-demolition-red hover:underline"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span className="font-mono text-sm">Link</span>
                      </a>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditDialog(event)}
                    className="p-2 text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                    data-testid={`edit-event-${event.id}`}
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(event)}
                    className="p-2 text-white/50 hover:text-red-500 hover:bg-white/10 transition-colors"
                    data-testid={`delete-event-${event.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && events.length === 0 && (
        <div className="text-center py-12 bg-demolition-paper border border-white/10">
          <Calendar className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="font-mono text-white/50">No events found. Create your first event!</p>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-demolition-paper border-white/10 max-w-lg" data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle className="font-russo text-2xl text-white uppercase tracking-tighter">
              {editingEvent ? "EDIT EVENT" : "CREATE EVENT"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label className="font-teko text-lg text-white uppercase tracking-wider">
                Event Title
              </Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="Event name"
                className="bg-demolition-subtle border-white/10 text-white font-mono"
                data-testid="event-title-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-teko text-lg text-white uppercase tracking-wider">
                  Date
                </Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className="bg-demolition-subtle border-white/10 text-white font-mono"
                  data-testid="event-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-teko text-lg text-white uppercase tracking-wider">
                  Location
                </Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                  placeholder="City, Venue"
                  className="bg-demolition-subtle border-white/10 text-white font-mono"
                  data-testid="event-location-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="font-teko text-lg text-white uppercase tracking-wider">
                External URL (Optional)
              </Label>
              <Input
                type="url"
                value={formData.external_url}
                onChange={(e) => setFormData({ ...formData, external_url: e.target.value })}
                placeholder="https://..."
                className="bg-demolition-subtle border-white/10 text-white font-mono"
                data-testid="event-url-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-teko text-lg text-white uppercase tracking-wider">
                Description (Optional)
              </Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Event description..."
                rows={3}
                className="bg-demolition-subtle border-white/10 text-white font-mono resize-none"
                data-testid="event-description-input"
              />
            </div>

            <DialogFooter className="gap-3">
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="px-6 py-2 border border-white/20 text-white/70 font-teko text-lg uppercase tracking-wider hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-demolition-red text-white font-teko text-lg uppercase tracking-wider hover:bg-demolition-red-hover transition-colors disabled:opacity-50"
                data-testid="save-event-btn"
              >
                {saveButtonLabel}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
